import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addLink,
  addMeasurement,
  archiveKpi,
  approveMeasurement,
  createKpi,
  exportKpiCsv,
  exportKpiPdf,
  createAlert,
  deleteAlert,
  deleteLink,
  getKpi,
  listAlerts,
  listLinks,
  listMeasurements,
  listThresholds,
  saveThresholds,
  restoreKpi,
  updateAlert,
  updateFormula,
  updateKpi,
} from "../../api/kpi.api";
import KpiBadge from "../../components/kpi/KpiBadge";
import KpiTrend from "../../components/kpi/KpiTrend";
import KpiMeasurementsTable from "../../components/kpi/KpiMeasurementsTable";
import KpiLinkModal from "../../components/kpi/KpiLinkModal";
import PinButton from "../../components/pins/PinButton";
import { KPICopy } from "../../copy/kpiCopy";
import { KPI_CATEGORY_OPTIONS, getKpiSubcategoryOptions } from "../../constants/kpiOptions";
import { useAuth } from "../../useAuth";
import { RoleEnum, hasRole } from "../../constants/roles";
import { canEditKpis, canManageKpis } from "../../accessControl";
import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { useDestructiveAction } from "../../destructiveActions";
import useRegisterRecentView from "../../features/recentViews/useRegisterRecentView";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  createKpiUnitFieldState,
  formatKpiValueWithUnit,
  KPI_UNIT_CUSTOM_MAX_LENGTH,
  KPI_UNIT_OPTIONS,
  KPI_UNIT_OTHER_VALUE,
  resolveKpiUnitApiValue,
  validateKpiUnitFieldState,
} from "../../utils/kpiUnits";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardStyle,
} from "../../ui/pagePatterns";

const tabs = ["Overview", "Measurements", "Links", "Alerts", "Settings"];

const normalizeComparator = (value) => {
  const map = { ">": "gt", ">=": "gte", "<": "lt", "<=": "lte" };
  return map[value] || value || "between";
};

const ALERT_TYPE_OPTIONS = [
  { value: "threshold_cross", label: "Threshold crossing" },
  { value: "stale", label: "Stale KPI" },
];

const THRESHOLD_CROSS_OPTIONS = [
  { value: "green_to_yellow", label: "Green to Yellow" },
  { value: "yellow_to_red", label: "Yellow to Red" },
  { value: "any_to_red", label: "Any to Red" },
];

const RECIPIENT_OPTIONS = [
  { value: "self", label: "Me" },
  { value: `role:${RoleEnum.MANAGER}`, label: "Managers" },
  { value: `role:${RoleEnum.ADMIN}`, label: "Admins" },
];

const panelStyle = {
  ...surfaceCardStyle,
  padding: spacing.xl + spacing.sm,
  borderRadius: radius.md,
};

const mutedTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.sm,
};

const helperTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const topNoticeStyle = {
  ...surfaceCardStyle,
  padding: spacing.xl,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  boxShadow: shadows.xs,
  display: "grid",
  gap: spacing.xs,
};

const tabButtonStyle = (active) => ({
  padding: "10px 12px",
  borderRadius: radius.sm,
  border: active ? `1px solid ${colors.accent}` : `1px solid ${colors.border}`,
  background: active ? colors.accentSubtle : colors.surface,
  color: active ? colors.brandPrimary : colors.textSecondary,
});

export default function KpiDetail({ kpiId = null, onClose, onUpdated }) {
  const { id: routeId } = useParams();
  const resolvedId = kpiId ?? routeId;
  const isNew = !resolvedId || resolvedId === "new";
  const embedded = Boolean(kpiId);
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  const canEdit = useMemo(() => canEditKpis(user), [user]);
  const canManage = useMemo(() => canManageKpis(user), [user]);
  const canExport = useMemo(() => {
    return hasRole(user?.role, [RoleEnum.ADMIN, RoleEnum.MANAGER, RoleEnum.EXEC]);
  }, [user]);

  const [activeTab, setActiveTab] = useState("Overview");
  const [kpi, setKpi] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [confirmAlertId, setConfirmAlertId] = useState(null);
  const [confirmLink, setConfirmLink] = useState(null);
  const [confirmThresholdReplace, setConfirmThresholdReplace] = useState(false);
  const [confirmMeasurement, setConfirmMeasurement] = useState(null);
  const [pendingAlertReplace, setPendingAlertReplace] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [alertDraft, setAlertDraft] = useState({
    rule_type: "threshold_cross",
    threshold_level: "any_to_red",
    stale_days: "",
    enabled: true,
    recipients: ["self"],
  });

  const [form, setForm] = useState({
    name: "",
    description: "",
    owner_id: "",
    direction: "higher",
    frequency: "",
    category: "",
    subcategory: "",
    target_value: "",
    requires_approval: false,
  });
  const [unitField, setUnitField] = useState(() => createKpiUnitFieldState(null));
  const [saveError, setSaveError] = useState("");
  useRegisterRecentView({
    itemType: "kpi",
    itemId: kpi?.id || resolvedId,
    title: kpi?.name,
    secondaryText: kpi?.category || kpi?.status || null,
    enabled: Boolean(kpi?.id && kpi?.name),
  });

  const [measurementDraft, setMeasurementDraft] = useState({
    measured_at: new Date().toISOString().slice(0, 16),
    value: "",
    source: "manual",
  });
  const { action: thresholdsReplaceAction } = useDestructiveAction("KPI_THRESHOLDS_REPLACE");
  const { action: alertRulesReplaceAction } = useDestructiveAction("KPI_ALERT_RULES_REPLACE");

  const trendValues = useMemo(() => measurements.slice(0, 12).map((m) => m.value).reverse(), [measurements]);
  const isArchived = String(kpi?.status || "").toLowerCase() === "archived";
  const isReadOnly = !canEdit || isArchived;
  const unitValidationError = validateKpiUnitFieldState(unitField);

  const handleUnitSelectionChange = (value) => {
    setUnitField((prev) => ({
      ...prev,
      selectedKey: value,
      isDirty: true,
    }));
    setSaveError("");
  };

  const handleCustomUnitChange = (value) => {
    setUnitField((prev) => ({
      ...prev,
      customValue: value,
      isDirty: true,
    }));
    setSaveError("");
  };

  const load = () => {
    if (isNew) return;
    setLoadError("");
    setSaveError("");
    Promise.all([getKpi(resolvedId), listThresholds(resolvedId), listMeasurements(resolvedId), listLinks(resolvedId)])
      .then(([kpiRes, thRes, measRes, linkRes]) => {
        setKpi(kpiRes.data);
        setForm({
          name: kpiRes.data.name,
          description: kpiRes.data.description || "",
          owner_id: kpiRes.data.owner_id || "",
          direction: kpiRes.data.direction || "higher",
          frequency: kpiRes.data.frequency || "",
          category: kpiRes.data.category || "",
          subcategory: kpiRes.data.subcategory || "",
          target_value: kpiRes.data.target_value || "",
          requires_approval: Boolean(kpiRes.data.requires_approval),
        });
        setUnitField(createKpiUnitFieldState(kpiRes.data.unit));
        setThresholds(
          (thRes.data || []).map((item) => ({
            ...item,
            comparator: normalizeComparator(item.comparator),
          }))
        );
        setMeasurements(measRes.data || []);
        setLinks(linkRes.data || []);
      })
      .catch((err) => {
        setLoadError(err?.response?.data?.detail || err?.message || "Unable to load KPI details.");
      });

    setAlertsLoading(true);
    listAlerts(resolvedId)
      .then((res) => setAlerts(res.data || []))
      .catch(() => setAlerts([]))
      .finally(() => setAlertsLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId]);

  const handleSave = async () => {
    if (isReadOnly) return;
    if (unitValidationError) {
      setSaveError(unitValidationError);
      return;
    }
    setSaveError("");
    const payload = {
      name: form.name,
      description: form.description,
      owner_id: form.owner_id,
      unit: resolveKpiUnitApiValue(unitField),
      direction: form.direction,
      frequency: form.frequency,
      category: form.category,
      subcategory: form.subcategory,
      target_value: form.target_value ? Number(form.target_value) : null,
      requires_approval: Boolean(form.requires_approval),
    };
    try {
      if (isNew) {
        const res = await createKpi(payload);
        if (embedded) {
          if (onUpdated) onUpdated();
        } else {
          navigate(`/kpis/${res.data.id}`);
        }
      } else {
        const res = await updateKpi(resolvedId, payload);
        setKpi(res.data);
        setUnitField(createKpiUnitFieldState(res.data.unit));
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error("Save failed", err);
      setSaveError(err?.response?.data?.detail || err?.message || "Save failed.");
    }
  };

  const handleAddMeasurement = async () => {
    if (isReadOnly) return;
    try {
      const payload = {
        measured_at: new Date(measurementDraft.measured_at).toISOString(),
        value: Number(measurementDraft.value),
        source: measurementDraft.source,
      };
      await addMeasurement(resolvedId, payload);
      setMeasurementDraft((prev) => ({ ...prev, value: "" }));
      load();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Measurement failed", err);
    }
  };

  const handleApprove = async (mId) => {
    if (!canManage || isArchived) return;
    await approveMeasurement(resolvedId, mId);
    load();
    if (onUpdated) onUpdated();
  };

  const handleSaveThresholds = async () => {
    if (isReadOnly) return;
    await saveThresholds(
      resolvedId,
      thresholds.map((t, idx) => ({ ...t, sort_order: idx }))
    );
    load();
    if (onUpdated) onUpdated();
  };

  const handleAddLink = async ({ entity_type, entity_id }) => {
    if (isReadOnly) return;
    await addLink(resolvedId, { entity_type, entity_id });
    load();
    if (onUpdated) onUpdated();
  };

  const handleRemoveLink = async (link) => {
    if (isReadOnly) return;
    await deleteLink(resolvedId, { entity_type: link.entity_type, entity_id: link.entity_id });
    load();
    if (onUpdated) onUpdated();
  };

  const handleArchive = async () => {
    if (!canManage || isArchived) return;
    await archiveKpi(resolvedId);
    if (onUpdated) onUpdated();
    if (embedded) {
      if (onClose) onClose();
      return;
    }
    navigate("/kpis");
  };

  const handleRestore = async () => {
    if (!canManage) return;
    await restoreKpi(resolvedId);
    if (onUpdated) onUpdated();
  };

  const handleFormulaSave = async (expression) => {
    if (isReadOnly) return;
    await updateFormula(resolvedId, { expression });
    load();
    if (onUpdated) onUpdated();
  };

  const downloadBlob = (blob, filename) => {
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (!canExport || isNew) return;
    try {
      const res = await exportKpiCsv(resolvedId);
      downloadBlob(res.data, `kpi-${resolvedId}.csv`);
    } catch (err) {
      console.error("Export CSV failed", err);
    }
  };

  const handleExportPdf = async () => {
    if (!canExport || isNew) return;
    try {
      const res = await exportKpiPdf(resolvedId);
      downloadBlob(res.data, `kpi-${resolvedId}.pdf`);
    } catch (err) {
      console.error("Export PDF failed", err);
    }
  };

  const toggleRecipient = (current, value) => {
    if (current.includes(value)) {
      return current.filter((item) => item !== value);
    }
    return [...current, value];
  };

  const buildAlertPayload = (draft) => ({
    rule_type: draft.rule_type,
    threshold_level: draft.rule_type === "threshold_cross" ? draft.threshold_level : null,
    stale_days: draft.rule_type === "stale" && draft.stale_days ? Number(draft.stale_days) : null,
    enabled: Boolean(draft.enabled),
    recipients: draft.recipients,
  });

  const handleUpdateAlert = async (alertId, updates) => {
    if (isReadOnly) return;
    await updateAlert(resolvedId, alertId, updates);
    load();
  };

  const handleDeleteAlert = async (alertId) => {
    if (isReadOnly) return;
    await deleteAlert(resolvedId, alertId);
    load();
  };

  if (!isNew && loadError && !kpi) {
    return (
      <div style={pageShellStyle}>
        <PageState
          state="error"
          align="left"
          title="KPI details are unavailable"
          description={`${loadError} Refresh the KPI to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={load}>
              Refresh
            </Button>
          }
        />
      </div>
    );
  }

  if (!isNew && !kpi) {
    return (
      <div style={pageShellStyle}>
        <PageState
          state="loading"
          align="left"
          title="Loading KPI"
          description="Glyder is preparing the KPI summary, measurements, thresholds, and linked records."
        />
      </div>
    );
  }

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <h2 style={pageTitleStyle}>{isNew ? "New KPI" : kpi?.name || "KPI"}</h2>
            {!isNew && isArchived && (
              <span
                style={{
                  fontSize: typography.size.xs,
                  padding: "3px 8px",
                  borderRadius: radius.pill,
                  background: colors.surfaceMuted,
                  color: colors.textMuted,
                }}
              >
                Archived
              </span>
            )}
          </div>
          {!isNew && <p style={pageSubtitleStyle}>{kpi?.description}</p>}
        </div>
        {!isNew && (
          <div style={pageHeaderActionsStyle}>
            <PinButton
              itemType="kpi"
              itemId={resolvedId}
              summary={{
                title: kpi?.name || "KPI",
                secondaryText: [kpi?.category, kpi?.latest_value !== null && kpi?.latest_value !== undefined ? `Latest: ${kpi.latest_value}` : null]
                  .filter(Boolean)
                  .join(" | "),
                status: kpi?.status || null,
                ownerDisplay: kpi?.owner_id || null,
                updatedAt: kpi?.updated_at || null,
              }}
            />
            {canExport && (
              <>
                <Button onClick={handleExportCsv} variant="secondary">
                  Export CSV
                </Button>
                <Button onClick={handleExportPdf} variant="secondary">
                  Export PDF
                </Button>
              </>
            )}
            {isArchived ? (
              <Button
                onClick={handleRestore}
                disabled={!canManage}
                title={canManage ? undefined : "Manage access required"}
                variant="secondary"
              >
                Restore
              </Button>
            ) : (
              <Button
                onClick={() => setConfirmArchiveOpen(true)}
                disabled={!canManage}
                title={canManage ? undefined : "Manage access required"}
                variant="destructive"
              >
                Archive
              </Button>
            )}
          </div>
        )}
      </div>
      <div style={topNoticeStyle}>
        {!isNew && isArchived && <div style={helperTextStyle}>{KPICopy.archivedNotice}</div>}
        {!isNew && !isArchived && !canEdit && <div style={helperTextStyle}>{KPICopy.readOnlyNotice}</div>}
        <div style={mutedTextStyle}>{KPICopy.betaNotice}</div>
        <div style={helperTextStyle}>{KPICopy.notAvailable}</div>
        {!isNew && canExport && <div style={helperTextStyle}>Exports may contain sensitive data.</div>}
      </div>

      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {tabs.map((tab) => (
          <Button
            key={tab}
            onClick={() => setActiveTab(tab)}
            variant="secondary"
            style={tabButtonStyle(activeTab === tab)}
          >
            {tab}
          </Button>
        ))}
      </div>

      {activeTab === "Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>
          <div style={panelStyle}>
            <div style={{ marginBottom: "10px", ...mutedTextStyle }}>
              {KPICopy.overviewHelper}
            </div>
            <div style={{ marginBottom: "12px", ...helperTextStyle }}>
              {KPICopy.overviewEvalHint}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <label>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Name</div>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  disabled={isReadOnly}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                />
              </label>
              <label>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Owner</div>
                <input
                  value={form.owner_id || ""}
                  onChange={(e) => setForm((p) => ({ ...p, owner_id: e.target.value }))}
                  disabled={isReadOnly}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                />
              </label>
              <label>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Unit</div>
                <select
                  value={unitField.selectedKey}
                  onChange={(e) => handleUnitSelectionChange(e.target.value)}
                  disabled={isReadOnly}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                >
                  {KPI_UNIT_OPTIONS.map((option) => (
                    <option key={option.value || "none"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {unitField.selectedKey === KPI_UNIT_OTHER_VALUE && (
                <label>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>Custom unit</div>
                  <input
                    value={unitField.customValue}
                    onChange={(e) => handleCustomUnitChange(e.target.value)}
                    disabled={isReadOnly}
                    maxLength={KPI_UNIT_CUSTOM_MAX_LENGTH}
                    placeholder="Enter custom unit"
                    style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                  />
                  {unitField.isDirty && unitValidationError && (
                    <div style={{ marginTop: "4px", color: colors.danger, fontSize: typography.size.xs }}>
                      {unitValidationError}
                    </div>
                  )}
                </label>
              )}
              <label>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Category</div>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm((prev) => {
                      const nextCategory = e.target.value;
                      const options = getKpiSubcategoryOptions(nextCategory);
                      const hasCurrent = options.some((opt) => opt.value === prev.subcategory);
                      return {
                        ...prev,
                        category: nextCategory,
                        subcategory: hasCurrent ? prev.subcategory : "",
                      };
                    })
                  }
                  disabled={isReadOnly}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                >
                  {KPI_CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value || "none"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Subcategory</div>
                <select
                  value={form.subcategory}
                  onChange={(e) => setForm((p) => ({ ...p, subcategory: e.target.value }))}
                  disabled={isReadOnly || !form.category}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                >
                  <option value="">None</option>
                  {getKpiSubcategoryOptions(form.category).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              {isNew && (
                <label>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>Frequency</div>
                  <select
                    value={form.frequency}
                    onChange={(e) => setForm((p) => ({ ...p, frequency: e.target.value }))}
                    disabled={isReadOnly}
                    style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                  >
                    <option value="">None</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </label>
              )}
              <label style={{ gridColumn: "span 2" }}>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>Description</div>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  disabled={isReadOnly}
                  rows={3}
                  style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                />
              </label>
            </div>
            {saveError && (
              <div style={{ marginTop: "12px", color: colors.danger, fontSize: typography.size.xs }}>{saveError}</div>
            )}
            <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button
                onClick={handleSave}
                disabled={isReadOnly}
                title={isReadOnly ? "Read-only access" : undefined}
                variant="primary"
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "#10b2aa",
                  color: "white",
                  border: "none",
                  opacity: isReadOnly ? 0.6 : 1,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                }}
              >
                Save
              </Button>
            </div>
          </div>
          {!isNew && (
          <div style={panelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={helperTextStyle}>Latest value</div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: colors.textPrimary }}>
                  {kpi?.latest_value !== null && kpi?.latest_value !== undefined
                    ? formatKpiValueWithUnit(kpi.latest_value, kpi.unit)
                    : "-"}
                </div>
                <div style={helperTextStyle}>
                  {kpi?.latest_measurement_at ? new Date(kpi.latest_measurement_at).toLocaleString() : "No measurements"}
                </div>
              </div>
                <KpiBadge value={kpi?.latest_rag} />
              </div>
              <div style={{ marginTop: "12px" }}>
                {trendValues.length > 1 ? (
                  <KpiTrend points={trendValues} />
                ) : (
                  <div style={helperTextStyle}>Add more measurements to show a trend.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "Measurements" && !isNew && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}>
          <div style={panelStyle}>
            <div style={{ marginBottom: "8px", ...mutedTextStyle }}>
              {KPICopy.measurementsHelper}
            </div>
            <h4 style={{ marginTop: 0 }}>Add measurement</h4>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <input
                type="datetime-local"
                value={measurementDraft.measured_at}
                onChange={(e) => setMeasurementDraft((p) => ({ ...p, measured_at: e.target.value }))}
                disabled={isReadOnly}
                style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
              />
              <input
                type="number"
                placeholder="Value"
                value={measurementDraft.value}
                onChange={(e) => setMeasurementDraft((p) => ({ ...p, value: e.target.value }))}
                disabled={isReadOnly}
                style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
              />
              <Button
                onClick={handleAddMeasurement}
                disabled={isReadOnly}
                title={isReadOnly ? "Read-only access" : undefined}
                variant="primary"
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "#10b2aa",
                  color: "white",
                  border: "none",
                  opacity: isReadOnly ? 0.6 : 1,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                }}
              >
                Add
              </Button>
            </div>
          </div>

          <div style={panelStyle}>
            <KpiMeasurementsTable measurements={measurements} unit={kpi?.unit} />
            <div style={{ marginTop: "8px", ...helperTextStyle }}>
              Click pending rows to approve.
            </div>
            <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {measurements
                .filter((m) => !m.approved)
                .map((m) => (
                  <Button
                    key={m.id}
                    onClick={() => setConfirmMeasurement(m)}
                    disabled={!canManage || isArchived}
                    title={!canManage ? "Manage access required" : isArchived ? "Archived KPI" : undefined}
                    variant="secondary"
                    style={{
                      padding: "8px 10px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      background: "#f9fafb",
                      opacity: !canManage || isArchived ? 0.6 : 1,
                      cursor: !canManage || isArchived ? "not-allowed" : "pointer",
                    }}
                  >
                    Approve measurement {m.id}
                  </Button>
                ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "Links" && !isNew && (
        <div style={panelStyle}>
          <div style={{ marginBottom: "8px", ...mutedTextStyle }}>
            {KPICopy.linksHelper}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 style={{ margin: 0 }}>Linked entities</h4>
            <Button
              onClick={() => setLinkModalOpen(true)}
              disabled={isReadOnly}
              title={isReadOnly ? "Read-only access" : undefined}
              variant="secondary"
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                background: "#f3f4f6",
                opacity: isReadOnly ? 0.6 : 1,
                cursor: isReadOnly ? "not-allowed" : "pointer",
              }}
            >
              Add link
            </Button>
          </div>
          <div style={{ marginTop: "8px" }}>
            {links.map((link) => (
              <div
                key={`${link.entity_type}-${link.entity_id}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{link.entity_type}</div>
                  <div style={helperTextStyle}>ID: {link.entity_id}</div>
                </div>
                {confirmLink?.entity_type === link.entity_type && confirmLink?.entity_id === link.entity_id ? (
                  <DestructiveConfirm
                    actionId="KPI_LINK_DELETE"
                    open
                    resourceDisplayName={`${link.entity_type} ${link.entity_id}`}
                    variantOverride="inline"
                    onCancel={() => setConfirmLink(null)}
                    onConfirm={async () => {
                      await handleRemoveLink(link);
                      setConfirmLink(null);
                      return async () => {
                        await handleAddLink({ entity_type: link.entity_type, entity_id: link.entity_id });
                      };
                    }}
                  />
                ) : (
                  <Button
                    onClick={() => setConfirmLink(link)}
                    disabled={isReadOnly}
                    title={isReadOnly ? "Read-only access" : undefined}
                    variant="destructive"
                    style={{
                      borderRadius: "6px",
                      padding: "6px 8px",
                      opacity: isReadOnly ? 0.6 : 1,
                      cursor: isReadOnly ? "not-allowed" : "pointer",
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            {links.length === 0 && <div style={mutedTextStyle}>No links yet.</div>}
          </div>
        </div>
      )}

      {activeTab === "Alerts" && !isNew && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}>
          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Operational Alerts</h4>
            <div style={mutedTextStyle}>
              Alert rules support threshold and stale-data monitoring for this KPI. Delivery behavior depends on environment configuration.
            </div>
            {!canEdit && (
              <div style={{ marginTop: "6px", ...helperTextStyle, color: colors.textSubtle }}>
                {KPICopy.readOnlyNotice}
              </div>
            )}
          </div>

          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Configured rules</h4>
            {alertRulesReplaceAction && (
              <div
                style={{
                  border: "1px solid #fde68a",
                  background: "#fffbeb",
                  color: "#92400e",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 12,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 600 }}>Implicit replacement</div>
                <div>{alertRulesReplaceAction.description}</div>
                <div>Scope: {alertRulesReplaceAction.scope}</div>
                <div>Omitted alert rules will be removed when you save.</div>
              </div>
            )}
            {alertsLoading && <div style={helperTextStyle}>Loading alerts...</div>}
            {!alertsLoading && alerts.length === 0 && (
              <div style={helperTextStyle}>No alert rules configured yet.</div>
            )}
            {!alertsLoading &&
              alerts.map((rule) => (
                <div
                  key={rule.id}
                  style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: radius.md,
                    padding: "10px",
                    marginTop: "10px",
                    background: colors.surfaceSubtle,
                  }}
                >
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>Rule type</span>
                      <select
                        value={rule.rule_type}
                        onChange={(e) =>
                          setAlerts((prev) =>
                            prev.map((item) =>
                              item.id === rule.id
                                ? {
                                    ...item,
                                    rule_type: e.target.value,
                                    threshold_level: e.target.value === "threshold_cross" ? "any_to_red" : null,
                                  }
                                : item
                            )
                          )
                        }
                        disabled={isReadOnly}
                        style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                      >
                        {ALERT_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {rule.rule_type === "threshold_cross" && (
                      <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>Crossing</span>
                        <select
                          value={rule.threshold_level || "any_to_red"}
                          onChange={(e) =>
                            setAlerts((prev) =>
                              prev.map((item) =>
                                item.id === rule.id ? { ...item, threshold_level: e.target.value } : item
                              )
                            )
                          }
                          disabled={isReadOnly}
                          style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                        >
                          {THRESHOLD_CROSS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    {rule.rule_type === "stale" && (
                      <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>Stale days</span>
                        <input
                          type="number"
                          value={rule.stale_days ?? ""}
                          onChange={(e) =>
                            setAlerts((prev) =>
                              prev.map((item) =>
                                item.id === rule.id ? { ...item, stale_days: e.target.value } : item
                              )
                            )
                          }
                          disabled={isReadOnly}
                          style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", width: "120px" }}
                        />
                      </label>
                    )}

                    <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "18px" }}>
                      <input
                        type="checkbox"
                        checked={Boolean(rule.enabled)}
                        onChange={(e) =>
                          setAlerts((prev) =>
                            prev.map((item) =>
                              item.id === rule.id ? { ...item, enabled: e.target.checked } : item
                            )
                          )
                        }
                        disabled={isReadOnly}
                      />
                      <span style={{ fontSize: "12px" }}>Enabled</span>
                    </label>
                  </div>

                  <div style={{ marginTop: "10px" }}>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>Recipients</div>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      {RECIPIENT_OPTIONS.map((opt) => {
                        const selected = (rule.recipients || []).includes(opt.value);
                        return (
                          <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() =>
                                setAlerts((prev) =>
                                  prev.map((item) =>
                                    item.id === rule.id
                                      ? {
                                          ...item,
                                          recipients: toggleRecipient(item.recipients || [], opt.value),
                                        }
                                      : item
                                  )
                                )
                              }
                              disabled={isReadOnly}
                            />
                            <span style={{ fontSize: "12px" }}>{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                    <Button
                      onClick={() =>
                        setPendingAlertReplace({
                          alertId: rule.id,
                          payload: {
                            rule_type: rule.rule_type,
                            threshold_level:
                              rule.rule_type === "threshold_cross" ? rule.threshold_level : null,
                            stale_days:
                              rule.rule_type === "stale" && rule.stale_days !== "" ? Number(rule.stale_days) : null,
                            enabled: Boolean(rule.enabled),
                            recipients: rule.recipients || [],
                          },
                        })
                      }
                      disabled={isReadOnly}
                      variant="destructive"
                      style={{
                        padding: "8px 10px",
                        borderRadius: "6px",
                        opacity: isReadOnly ? 0.6 : 1,
                        cursor: isReadOnly ? "not-allowed" : "pointer",
                      }}
                    >
                      Replace alert rules
                    </Button>
                    <Button
                      onClick={() => setConfirmAlertId(rule.id)}
                      disabled={isReadOnly}
                      variant="destructive"
                      style={{
                        padding: "8px 10px",
                        borderRadius: "6px",
                        opacity: isReadOnly ? 0.6 : 1,
                        cursor: isReadOnly ? "not-allowed" : "pointer",
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
          </div>

          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Create alert rule</h4>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>Rule type</span>
                <select
                  value={alertDraft.rule_type}
                  onChange={(e) =>
                    setAlertDraft((prev) => ({
                      ...prev,
                      rule_type: e.target.value,
                      threshold_level: e.target.value === "threshold_cross" ? "any_to_red" : "",
                    }))
                  }
                  disabled={isReadOnly}
                  style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                >
                  {ALERT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              {alertDraft.rule_type === "threshold_cross" && (
                <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>Crossing</span>
                  <select
                    value={alertDraft.threshold_level}
                    onChange={(e) => setAlertDraft((prev) => ({ ...prev, threshold_level: e.target.value }))}
                    disabled={isReadOnly}
                    style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                  >
                    {THRESHOLD_CROSS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {alertDraft.rule_type === "stale" && (
                <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>Stale days</span>
                  <input
                    type="number"
                    value={alertDraft.stale_days}
                    onChange={(e) => setAlertDraft((prev) => ({ ...prev, stale_days: e.target.value }))}
                    disabled={isReadOnly}
                    style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", width: "120px" }}
                  />
                </label>
              )}

              <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "18px" }}>
                <input
                  type="checkbox"
                  checked={alertDraft.enabled}
                  onChange={(e) => setAlertDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                  disabled={isReadOnly}
                />
                <span style={{ fontSize: "12px" }}>Enabled</span>
              </label>
            </div>

            <div style={{ marginTop: "10px" }}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>Recipients</div>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {RECIPIENT_OPTIONS.map((opt) => {
                  const selected = alertDraft.recipients.includes(opt.value);
                  return (
                    <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() =>
                          setAlertDraft((prev) => ({
                            ...prev,
                            recipients: toggleRecipient(prev.recipients, opt.value),
                          }))
                        }
                        disabled={isReadOnly}
                      />
                      <span style={{ fontSize: "12px" }}>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() =>
                  setPendingAlertReplace({
                    alertId: null,
                    payload: buildAlertPayload(alertDraft),
                    isCreate: true,
                  })
                }
                disabled={isReadOnly}
                variant="primary"
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#10b2aa",
                  color: "white",
                  opacity: isReadOnly ? 0.6 : 1,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                }}
              >
                Create alert
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "Settings" && !isNew && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}>
          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Governance</h4>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>Frequency</span>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>{KPICopy.settingsFrequency}</span>
                <select
                  value={form.frequency}
                  onChange={(e) => setForm((p) => ({ ...p, frequency: e.target.value }))}
                  disabled={isReadOnly}
                  style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", minWidth: "160px" }}
                >
                  <option value="">None</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={form.requires_approval}
                  onChange={(e) => setForm((p) => ({ ...p, requires_approval: e.target.checked }))}
                  disabled={isReadOnly}
                  aria-label="Requires approval for measurements"
                />
                <span style={{ fontSize: "13px" }}>Requires approval for measurements</span>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>{KPICopy.settingsApproval}</span>
              </label>
            </div>
            <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  onClick={handleSave}
                  disabled={isReadOnly}
                  title={isReadOnly ? "Read-only access" : undefined}
                  variant="secondary"
                  style={{
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    background: "#f9fafb",
                    opacity: isReadOnly ? 0.6 : 1,
                    cursor: isReadOnly ? "not-allowed" : "pointer",
                  }}
                >
                  Save settings
                </Button>
                {isArchived ? (
                  <Button
                    onClick={handleRestore}
                    disabled={!canManage}
                    title={canManage ? undefined : "Manage access required"}
                    variant="secondary"
                  >
                    Restore KPI
                  </Button>
                ) : (
                  <Button
                    onClick={() => setConfirmArchiveOpen(true)}
                    disabled={!canManage}
                    title={canManage ? undefined : "Manage access required"}
                    variant="destructive"
                    style={{
                      borderRadius: "6px",
                      padding: "8px 10px",
                      opacity: canManage ? 1 : 0.6,
                      cursor: canManage ? "pointer" : "not-allowed",
                    }}
                  >
                    Archive KPI
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Thresholds</h4>
            <div style={{ marginBottom: "8px", ...mutedTextStyle }}>
              {KPICopy.settingsThresholds}
            </div>
            {thresholdsReplaceAction && (
              <div
                style={{
                  border: "1px solid #fde68a",
                  background: "#fffbeb",
                  color: "#92400e",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 12,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 600 }}>Implicit replacement</div>
                <div>{thresholdsReplaceAction.description}</div>
                <div>Scope: {thresholdsReplaceAction.scope}</div>
                <div>Omitted thresholds will be removed when you save.</div>
              </div>
            )}
            {thresholds.map((th, idx) => (
              <div key={idx} style={{ display: "flex", gap: "8px", marginBottom: "6px", alignItems: "center" }}>
                <input
                  value={th.level}
                  onChange={(e) =>
                    setThresholds((prev) => prev.map((item, i) => (i === idx ? { ...item, level: e.target.value } : item)))
                  }
                  disabled={isReadOnly}
                  placeholder="Level"
                  style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                />
                <select
                  value={th.comparator}
                  onChange={(e) =>
                    setThresholds((prev) => prev.map((item, i) => (i === idx ? { ...item, comparator: e.target.value } : item)))
                  }
                  disabled={isReadOnly}
                  style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
                >
                  <option value="between">between</option>
                  <option value="gte">gte</option>
                  <option value="lte">lte</option>
                  <option value="gt">gt</option>
                  <option value="lt">lt</option>
                </select>
                <input
                  type="number"
                  value={th.value_low ?? ""}
                  onChange={(e) =>
                    setThresholds((prev) =>
                      prev.map((item, i) => (i === idx ? { ...item, value_low: e.target.value ? Number(e.target.value) : null } : item))
                    )
                  }
                  disabled={isReadOnly}
                  placeholder="Low"
                  style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", width: "120px" }}
                />
                <input
                  type="number"
                  value={th.value_high ?? ""}
                  onChange={(e) =>
                    setThresholds((prev) =>
                      prev.map((item, i) => (i === idx ? { ...item, value_high: e.target.value ? Number(e.target.value) : null } : item))
                    )
                  }
                  disabled={isReadOnly}
                  placeholder="High"
                  style={{ padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db", width: "120px" }}
                />
                <Button
                  onClick={() => setThresholds((prev) => prev.filter((_, i) => i !== idx))}
                  disabled={isReadOnly}
                  title={isReadOnly ? "Read-only access" : undefined}
                  variant="destructive"
                  style={{
                    borderRadius: "6px",
                    padding: "6px 8px",
                    opacity: isReadOnly ? 0.6 : 1,
                    cursor: isReadOnly ? "not-allowed" : "pointer",
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              onClick={() => setThresholds((prev) => [...prev, { level: "green", comparator: "between", value_low: 0, value_high: 1 }])}
              disabled={isReadOnly}
              title={isReadOnly ? "Read-only access" : undefined}
              variant="secondary"
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                background: "#f9fafb",
                opacity: isReadOnly ? 0.6 : 1,
                cursor: isReadOnly ? "not-allowed" : "pointer",
              }}
            >
              Add threshold
            </Button>
            <div style={{ marginTop: "8px" }}>
              <Button
                onClick={() => setConfirmThresholdReplace(true)}
                disabled={isReadOnly}
                title={isReadOnly ? "Read-only access" : undefined}
                variant="destructive"
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  opacity: isReadOnly ? 0.6 : 1,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                }}
              >
                Replace thresholds
              </Button>
            </div>
          </div>

          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Formula</h4>
            <p style={mutedTextStyle}>Optional: compute KPI from other KPIs using formulas that reference the latest linked values.</p>
            <textarea
              defaultValue={kpi?.formula?.expression || ""}
              onBlur={(e) => handleFormulaSave(e.target.value)}
              disabled={isReadOnly}
              rows={3}
              style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #d1d5db" }}
            />
          </div>

          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Alert rules</h4>
            <div style={mutedTextStyle}>{KPICopy.alertsPlaceholder}</div>
          </div>

          <div style={panelStyle}>
            <h4 style={{ marginTop: 0 }}>Environment Notes</h4>
            <div style={mutedTextStyle}>{KPICopy.betaNotice}</div>
            <div style={{ marginTop: "6px", ...helperTextStyle }}>{KPICopy.notAvailable}</div>
          </div>
        </div>
      )}

      <DestructiveConfirm
        actionId="KPI_MEASUREMENT_APPROVE"
        open={Boolean(confirmMeasurement)}
        resourceDisplayName={confirmMeasurement ? `Measurement ${confirmMeasurement.id}` : undefined}
        context={{ scopeLabel: "Approve KPI measurement" }}
        onCancel={() => setConfirmMeasurement(null)}
        onConfirm={async () => {
          const pending = confirmMeasurement;
          setConfirmMeasurement(null);
          if (pending) {
            await handleApprove(pending.id);
          }
        }}
      />
      <DestructiveConfirm
        actionId="KPI_ARCHIVE"
        open={confirmArchiveOpen}
        resourceDisplayName={kpi?.name}
        onCancel={() => setConfirmArchiveOpen(false)}
        onConfirm={async () => {
          setConfirmArchiveOpen(false);
          await handleArchive();
          return handleRestore;
        }}
      />
      <DestructiveConfirm
        actionId="KPI_THRESHOLDS_REPLACE"
        open={confirmThresholdReplace}
        resourceDisplayName={kpi?.name}
        context={{ scopeLabel: "Replacing thresholds removes any omitted thresholds." }}
        onCancel={() => setConfirmThresholdReplace(false)}
        onConfirm={async () => {
          setConfirmThresholdReplace(false);
          await handleSaveThresholds();
        }}
      />
      <DestructiveConfirm
        actionId="KPI_ALERT_RULES_REPLACE"
        open={Boolean(pendingAlertReplace)}
        resourceDisplayName={kpi?.name}
        context={{ scopeLabel: "Saving rules replaces the current alert rule set." }}
        onCancel={() => setPendingAlertReplace(null)}
        onConfirm={async () => {
          const pending = pendingAlertReplace;
          setPendingAlertReplace(null);
          if (!pending) return;
          if (pending.alertId) {
            await handleUpdateAlert(pending.alertId, pending.payload);
          } else {
            await createAlert(resolvedId, pending.payload);
            setAlertDraft({
              rule_type: "threshold_cross",
              threshold_level: "any_to_red",
              stale_days: "",
              enabled: true,
              recipients: ["self"],
            });
            load();
          }
        }}
      />
      <DestructiveConfirm
        actionId="KPI_ALERT_DELETE"
        open={Boolean(confirmAlertId)}
        resourceDisplayName={kpi?.name}
        onCancel={() => setConfirmAlertId(null)}
        onConfirm={async () => {
          if (!confirmAlertId) return;
          const alertId = confirmAlertId;
          setConfirmAlertId(null);
          await handleDeleteAlert(alertId);
        }}
      />
      <KpiLinkModal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} onSelect={handleAddLink} />
    </div>
  );
}
